import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError, eur, offerTotals } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ArrowLeft, Download, Trash2 } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const STAGES = ["Lead", "Contatto", "Preventivo", "Vinto", "Perso"];
const STAGE_COLORS = { Lead: "#94A3B8", Contatto: "#0EA5E9", Preventivo: "#F59E0B", Vinto: "#10B981", Perso: "#EF4444" };

export default function OfferDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [offer, setOffer] = useState(null);
  const [company, setCompany] = useState(null);
  const [contact, setContact] = useState(null);

  const load = useCallback(() => api.get(`/offers/${id}`).then((r) => {
    setOffer(r.data);
    if (r.data.company_id) api.get(`/companies/${r.data.company_id}`).then((c) => setCompany(c.data)).catch(() => {});
    if (r.data.contact_id) api.get(`/contacts?company_id=${r.data.company_id}`).then((cs) => setContact(cs.data.find((x) => x.id === r.data.contact_id))).catch(() => {});
  }), [id]);
  useEffect(() => { load(); }, [load]);

  const changeStage = async (stage) => { await api.patch(`/offers/${id}/stage`, { stage }); toast.success("Fase aggiornata"); load(); };
  const del = async () => { await api.delete(`/offers/${id}`); toast.success("Offerta eliminata"); navigate("/offerte"); };

  const exportPDF = () => {
    const t = offerTotals(offer);
    const doc = new jsPDF();
    doc.setFillColor(10, 25, 47); doc.rect(0, 0, 210, 28, "F");
    doc.setTextColor(255); doc.setFont("helvetica", "bold"); doc.setFontSize(18); doc.text("LINEARCRM", 14, 15);
    doc.setFontSize(9); doc.setFont("helvetica", "normal"); doc.text("Attuatori Lineari — Offerta Commerciale", 14, 22);
    doc.setTextColor(0, 82, 204); doc.setFont("helvetica", "bold"); doc.setFontSize(12); doc.text(offer.number, 196, 15, { align: "right" });

    doc.setTextColor(15, 23, 42); doc.setFontSize(15); doc.setFont("helvetica", "bold"); doc.text(offer.title, 14, 42);

    doc.setFontSize(10); doc.setFont("helvetica", "bold"); doc.text("Cliente:", 14, 54);
    doc.setFont("helvetica", "normal");
    doc.text(company?.name || "—", 14, 60);
    if (company?.vat) doc.text(`P.IVA: ${company.vat}`, 14, 66);
    if ([company?.address, company?.city].filter(Boolean).length) doc.text([company?.address, company?.city].filter(Boolean).join(", "), 14, company?.vat ? 72 : 66);
    if (contact) doc.text(`Rif: ${contact.name}${contact.role ? " (" + contact.role + ")" : ""}`, 130, 60);
    if (offer.valid_until) doc.text(`Valida fino al: ${offer.valid_until}`, 130, 66);

    autoTable(doc, {
      startY: 82,
      head: [["Descrizione", "Q.tà", "Prezzo unit.", "Importo"]],
      body: (offer.items || []).map((i) => [i.description, String(i.quantity), eur(i.unit_price), eur(i.quantity * i.unit_price)]),
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [0, 82, 204], halign: "left" },
      columnStyles: { 1: { halign: "right" }, 2: { halign: "right" }, 3: { halign: "right" } },
    });

    let y = doc.lastAutoTable.finalY + 8;
    const rightVal = (label, val, bold) => {
      doc.setFont("helvetica", bold ? "bold" : "normal");
      doc.text(label, 140, y); doc.text(val, 196, y, { align: "right" }); y += 6;
    };
    rightVal("Subtotale", eur(t.subtotal));
    if (offer.discount_pct > 0) rightVal(`Sconto (${offer.discount_pct}%)`, "-" + eur(t.subtotal - t.afterDiscount));
    rightVal(`IVA (${offer.tax_pct}%)`, eur(t.tax));
    doc.setFontSize(12); rightVal("TOTALE", eur(t.total), true);

    if (offer.notes) { doc.setFontSize(9); doc.setFont("helvetica", "italic"); doc.setTextColor(100); doc.text("Note: " + offer.notes, 14, y + 6, { maxWidth: 120 }); }
    doc.save(`${offer.number}.pdf`);
  };

  if (!offer) return <div className="p-10 font-mono-label text-sm text-slate-400">CARICAMENTO...</div>;
  const t = offerTotals(offer);

  return (
    <div className="p-10">
      <button data-testid="back-btn" onClick={() => navigate("/offerte")} className="flex items-center gap-2 text-sm text-slate-500 hover:text-[#0052CC] transition-colors duration-200 mb-6">
        <ArrowLeft className="w-4 h-4" /> Torna alle offerte
      </button>

      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <div className="font-mono-label text-[10px] text-[#0052CC] mb-2">{offer.number}</div>
          <h1 className="font-display font-bold text-3xl tracking-tight text-slate-900">{offer.title}</h1>
        </div>
        <div className="flex items-center gap-2">
          <Select value={offer.stage} onValueChange={changeStage}>
            <SelectTrigger data-testid="offer-stage-change" className="w-40 rounded-sm bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Button data-testid="export-pdf-btn" onClick={exportPDF} className="bg-[#0052CC] hover:bg-[#003D99] rounded-sm"><Download className="w-4 h-4 mr-2" />Esporta PDF</Button>
          <AlertDialog>
            <AlertDialogTrigger asChild><Button data-testid="delete-offer-btn" variant="outline" className="rounded-sm text-red-600 border-red-200 hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button></AlertDialogTrigger>
            <AlertDialogContent className="rounded-sm">
              <AlertDialogHeader><AlertDialogTitle>Eliminare l'offerta?</AlertDialogTitle><AlertDialogDescription>Azione irreversibile.</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter><AlertDialogCancel className="rounded-sm">Annulla</AlertDialogCancel><AlertDialogAction onClick={del} className="bg-red-600 hover:bg-red-700 rounded-sm">Elimina</AlertDialogAction></AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <div className="font-mono-label text-[10px] text-slate-500">RIGHE OFFERTA</div>
            <span className="text-[10px] px-2 py-0.5 rounded-sm text-white font-medium" style={{ background: STAGE_COLORS[offer.stage] }}>{offer.stage}</span>
          </div>
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left font-mono-label text-[10px] text-slate-500 px-6 py-3">Descrizione</th>
              <th className="text-right font-mono-label text-[10px] text-slate-500 px-6 py-3">Qtà</th>
              <th className="text-right font-mono-label text-[10px] text-slate-500 px-6 py-3">Prezzo</th>
              <th className="text-right font-mono-label text-[10px] text-slate-500 px-6 py-3">Importo</th>
            </tr></thead>
            <tbody>
              {(offer.items || []).map((it, i) => (
                <tr key={i} className={i % 2 ? "bg-slate-50/40" : ""}>
                  <td className="px-6 py-3 text-slate-800">{it.description}</td>
                  <td className="px-6 py-3 text-right text-slate-600">{it.quantity}</td>
                  <td className="px-6 py-3 text-right text-slate-600">{eur(it.unit_price)}</td>
                  <td className="px-6 py-3 text-right font-medium text-slate-900">{eur(it.quantity * it.unit_price)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-6 py-4 border-t border-slate-200 space-y-1.5 text-sm">
            <div className="flex justify-between text-slate-600"><span>Subtotale</span><span>{eur(t.subtotal)}</span></div>
            {offer.discount_pct > 0 && <div className="flex justify-between text-slate-600"><span>Sconto ({offer.discount_pct}%)</span><span>-{eur(t.subtotal - t.afterDiscount)}</span></div>}
            <div className="flex justify-between text-slate-600"><span>IVA ({offer.tax_pct}%)</span><span>{eur(t.tax)}</span></div>
            <div className="flex justify-between pt-2 border-t border-slate-100 font-display font-bold text-lg text-slate-900"><span>Totale</span><span className="text-[#0052CC]">{eur(t.total)}</span></div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm p-6 space-y-4">
          <div><div className="font-mono-label text-[10px] text-slate-500 mb-1">CLIENTE</div>
            <div className="font-medium text-slate-900">{company?.name || "—"}</div>
            {company?.vat && <div className="text-sm text-slate-500">P.IVA {company.vat}</div>}</div>
          {contact && <div><div className="font-mono-label text-[10px] text-slate-500 mb-1">REFERENTE</div>
            <div className="text-sm text-slate-800">{contact.name}</div><div className="text-xs text-slate-500">{contact.role}</div></div>}
          {offer.valid_until && <div><div className="font-mono-label text-[10px] text-slate-500 mb-1">VALIDA FINO AL</div><div className="text-sm text-slate-800">{offer.valid_until}</div></div>}
          {offer.notes && <div><div className="font-mono-label text-[10px] text-slate-500 mb-1">NOTE</div><div className="text-sm text-slate-600">{offer.notes}</div></div>}
        </div>
      </div>
    </div>
  );
}
